import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Colors, Spacing, FontSizes, FontWeights, BorderRadius, Shadows } from '../constants/theme';
import UnifiedPageHeader from '../components/UnifiedPageHeader';
import { AnimatedCounter } from '../components/MicroInteractions';
import { Line, Bar, Radar } from 'recharts';

const Container = styled.div`
  background: ${Colors.background};
  min-height: 100vh;
  padding: 0;
`;

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: ${Spacing.xl}px;
`;

// Key Metrics Row
const MetricsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${Spacing.lg}px;
  margin-bottom: ${Spacing.xl}px;
`;

const MetricCard = styled.div<{ trend?: 'up' | 'down' | 'neutral' }>`
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.lg}px;
  padding: ${Spacing.lg}px;
  box-shadow: ${Shadows.sm};
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: ${Shadows.md};
    border-color: ${props => 
      props.trend === 'up' ? Colors.success :
      props.trend === 'down' ? Colors.danger :
      Colors.primary
    };
  }
`;

const MetricHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${Spacing.md}px;
`;

const MetricLabel = styled.div`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TrendIndicator = styled.div<{ trend: 'up' | 'down' | 'neutral' }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs}px;
  padding: ${Spacing.xs}px ${Spacing.sm}px;
  border-radius: ${BorderRadius.xs}px;
  font-size: ${FontSizes.xs}px;
  font-weight: ${FontWeights.semibold};
  
  ${props => {
    switch(props.trend) {
      case 'up':
        return `
          background: ${Colors.success}20;
          color: ${Colors.success};
        `;
      case 'down':
        return `
          background: ${Colors.danger}20;
          color: ${Colors.danger};
        `;
      default:
        return `
          background: ${Colors.borderLight};
          color: ${Colors.textTertiary};
        `;
    }
  }}
`;

const MetricValue = styled.div`
  font-size: ${FontSizes.xxxl}px;
  font-weight: ${FontWeights.bold};
  color: ${Colors.textPrimary};
  margin-bottom: ${Spacing.sm}px;
`;

const MetricSubtext = styled.div`
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textSecondary};
`;

// Main Grid Layout
const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${Spacing.xl}px;
  margin-bottom: ${Spacing.xl}px;
  
  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.lg}px;
  padding: ${Spacing.xl}px;
  box-shadow: ${Shadows.sm};
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${Spacing.lg}px;
`;

const CardTitle = styled.h3`
  font-size: ${FontSizes.lg}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textPrimary};
`;

const FilterButtons = styled.div`
  display: flex;
  gap: ${Spacing.sm}px;
`;

const FilterButton = styled.button<{ active?: boolean }>`
  padding: ${Spacing.xs}px ${Spacing.md}px;
  background: ${props => props.active ? Colors.primary : 'transparent'};
  color: ${props => props.active ? 'white' : Colors.textSecondary};
  border: 1px solid ${props => props.active ? Colors.primary : Colors.border};
  border-radius: ${BorderRadius.sm}px;
  font-size: ${FontSizes.xs}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? Colors.primaryDark : Colors.surfaceHover};
  }
`;

// Team Member List
const TeamList = styled.div`
  max-height: 400px;
  overflow-y: auto;
`;

const TeamMember = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.md}px;
  padding: ${Spacing.md}px;
  border-radius: ${BorderRadius.md}px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${Colors.surfaceHover};
  }
`;

const MemberAvatar = styled.div<{ score: number }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => 
    props.score >= 8 ? Colors.success :
    props.score >= 6 ? Colors.warning :
    Colors.danger
  };
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${FontWeights.semibold};
  font-size: ${FontSizes.sm}px;
`;

const MemberInfo = styled.div`
  flex: 1;
`;

const MemberName = styled.div`
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  color: ${Colors.textPrimary};
`;

const MemberRole = styled.div`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textSecondary};
`;

const MemberScore = styled.div<{ score: number }>`
  font-size: ${FontSizes.md}px;
  font-weight: ${FontWeights.semibold};
  color: ${props => 
    props.score >= 8 ? Colors.success :
    props.score >= 6 ? Colors.warning :
    Colors.danger
  };
`;

// Action Items Section
const ActionItemsSection = styled.div`
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.lg}px;
  padding: ${Spacing.xl}px;
  box-shadow: ${Shadows.sm};
`;

const ActionItem = styled.div<{ priority: 'high' | 'medium' | 'low' }>`
  display: flex;
  gap: ${Spacing.md}px;
  padding: ${Spacing.md}px;
  background: ${Colors.background};
  border-left: 3px solid ${props =>
    props.priority === 'high' ? Colors.danger :
    props.priority === 'medium' ? Colors.warning :
    Colors.info
  };
  border-radius: ${BorderRadius.sm}px;
  margin-bottom: ${Spacing.md}px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ActionIcon = styled.div<{ priority: 'high' | 'medium' | 'low' }>`
  width: 32px;
  height: 32px;
  border-radius: ${BorderRadius.sm}px;
  background: ${props =>
    props.priority === 'high' ? `${Colors.danger}20` :
    props.priority === 'medium' ? `${Colors.warning}20` :
    `${Colors.info}20`
  };
  color: ${props =>
    props.priority === 'high' ? Colors.danger :
    props.priority === 'medium' ? Colors.warning :
    Colors.info
  };
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${FontSizes.md}px;
`;

const ActionContent = styled.div`
  flex: 1;
`;

const ActionTitle = styled.div`
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  color: ${Colors.textPrimary};
  margin-bottom: ${Spacing.xs}px;
`;

const ActionDescription = styled.div`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textSecondary};
  line-height: 1.4;
`;

const ActionButton = styled.button`
  padding: ${Spacing.xs}px ${Spacing.sm}px;
  background: ${Colors.primary};
  color: white;
  border: none;
  border-radius: ${BorderRadius.xs}px;
  font-size: ${FontSizes.xs}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  align-self: center;
  
  &:hover {
    background: ${Colors.primaryDark};
  }
`;

// Sentiment Heatmap
const HeatmapContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: ${Spacing.xs}px;
  margin-top: ${Spacing.md}px;
`;

const HeatmapCell = styled.div<{ intensity: number }>`
  aspect-ratio: 1;
  border-radius: ${BorderRadius.xs}px;
  background: ${props => {
    const opacity = props.intensity / 10;
    return props.intensity >= 7 ? `rgba(76, 175, 80, ${opacity})` :
           props.intensity >= 5 ? `rgba(255, 193, 7, ${opacity})` :
           `rgba(244, 67, 54, ${opacity})`;
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${FontSizes.xs}px;
  font-weight: ${FontWeights.semibold};
  color: ${props => props.intensity >= 5 ? Colors.textPrimary : 'white'};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: ${Shadows.sm};
  }
`;

interface TeamMemberData {
  id: string;
  name: string;
  role: string;
  belongingScore: number;
  trend: 'up' | 'down' | 'neutral';
  lastPulseCheck: Date;
  recognitionsReceived: number;
  eventsAttended: number;
}

interface ActionItemData {
  id: string;
  priority: 'high' | 'medium' | 'low';
  title: string;
  description: string;
  memberName?: string;
}

const BelongingManagerInsightsScreen: React.FC = () => {
  const { t } = useTranslation();
  
  const [teamAverage, setTeamAverage] = useState(7.8);
  const [participationRate, setParticipationRate] = useState(72);
  const [recognitionsSent, setRecognitionsSent] = useState(45);
  const [atRiskCount, setAtRiskCount] = useState(3);
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'quarter'>('month');
  const [teamMembers, setTeamMembers] = useState<TeamMemberData[]>([]);
  const [actionItems, setActionItems] = useState<ActionItemData[]>([]);
  const [sentimentData, setSentimentData] = useState<number[][]>([]);

  useEffect(() => {
    loadManagerInsights();
  }, [timeRange]);

  const loadManagerInsights = () => {
    // Mock team member data
    const mockTeamMembers: TeamMemberData[] = [
      {
        id: '1',
        name: 'Alex Johnson',
        role: 'Senior Developer',
        belongingScore: 8.5,
        trend: 'up',
        lastPulseCheck: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
        recognitionsReceived: 8,
        eventsAttended: 4
      },
      {
        id: '2',
        name: 'Maria Garcia',
        role: 'Product Designer',
        belongingScore: 9.2,
        trend: 'up',
        lastPulseCheck: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
        recognitionsReceived: 12,
        eventsAttended: 6
      },
      {
        id: '3',
        name: 'Tom Chen',
        role: 'DevOps Engineer',
        belongingScore: 5.8,
        trend: 'down',
        lastPulseCheck: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
        recognitionsReceived: 2,
        eventsAttended: 1
      },
      {
        id: '4',
        name: 'Sarah Williams',
        role: 'QA Engineer',
        belongingScore: 7.3,
        trend: 'neutral',
        lastPulseCheck: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
        recognitionsReceived: 5,
        eventsAttended: 3
      },
      {
        id: '5',
        name: 'James Brown',
        role: 'Junior Developer',
        belongingScore: 6.1,
        trend: 'down',
        lastPulseCheck: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
        recognitionsReceived: 3,
        eventsAttended: 2
      }
    ];

    const mockActionItems: ActionItemData[] = [
      {
        id: '1',
        priority: 'high',
        title: 'Check in with Tom Chen',
        description: 'Belonging score dropped below 6.0. Schedule 1:1 to discuss concerns and support needs.',
        memberName: 'Tom Chen'
      },
      {
        id: '2',
        priority: 'medium',
        title: 'Recognize James for project completion',
        description: 'James hasn\'t received recognition in 2 weeks. Consider highlighting his recent contributions.',
        memberName: 'James Brown'
      },
      {
        id: '3',
        priority: 'low',
        title: 'Team building activity',
        description: 'Plan a team lunch or activity. 40% of team hasn\'t attended an event this month.'
      },
      {
        id: '4',
        priority: 'high',
        title: 'Follow up on pulse check responses',
        description: '3 team members haven\'t completed pulse checks in over a week.'
      }
    ];

    // Generate mock sentiment heatmap data (7 days x 4 weeks)
    const mockSentimentData = Array(4).fill(null).map(() =>
      Array(7).fill(null).map(() => Math.floor(Math.random() * 4) + 6)
    );

    setTeamMembers(mockTeamMembers);
    setActionItems(mockActionItems);
    setSentimentData(mockSentimentData);
  };

  const getInitials = (name: string): string => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const formatLastCheck = (date: Date): string => {
    const days = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    return `${days} days ago`;
  };

  return (
    <Container>
      <UnifiedPageHeader
        title={t('belonging.manager.title', 'Team Belonging Insights')}
        subtitle={t('belonging.manager.subtitle', 'Monitor and improve your team\'s sense of belonging')}
        metadata={{
          timestamp: new Date(),
          count: teamMembers.length,
          status: 'Live'
        }}
        actions={[
          {
            label: t('belonging.manager.export', 'Export Report'),
            onClick: () => console.log('Export report'),
            variant: 'secondary',
            icon: ''
          },
          {
            label: t('belonging.manager.scheduleCheckIn', 'Schedule Check-in'),
            onClick: () => console.log('Schedule check-in'),
            variant: 'primary',
            icon: ''
          }
        ]}
      />

      <ContentWrapper>
        {/* Key Metrics */}
        <MetricsRow>
          <MetricCard trend="up">
            <MetricHeader>
              <MetricLabel>{t('belonging.metrics.teamAverage', 'Team Average')}</MetricLabel>
              <TrendIndicator trend="up">↑ 5%</TrendIndicator>
            </MetricHeader>
            <MetricValue>
              <AnimatedCounter value={teamAverage} formatter={(v) => v.toFixed(1)} />
            </MetricValue>
            <MetricSubtext>Belonging Score (0-10)</MetricSubtext>
          </MetricCard>

          <MetricCard trend="up">
            <MetricHeader>
              <MetricLabel>{t('belonging.metrics.participation', 'Participation')}</MetricLabel>
              <TrendIndicator trend="up">↑ 12%</TrendIndicator>
            </MetricHeader>
            <MetricValue>
              <AnimatedCounter value={participationRate} />%
            </MetricValue>
            <MetricSubtext>Active in communities</MetricSubtext>
          </MetricCard>

          <MetricCard trend="neutral">
            <MetricHeader>
              <MetricLabel>{t('belonging.metrics.recognitions', 'Recognitions')}</MetricLabel>
              <TrendIndicator trend="neutral">→ 0%</TrendIndicator>
            </MetricHeader>
            <MetricValue>
              <AnimatedCounter value={recognitionsSent} />
            </MetricValue>
            <MetricSubtext>Given this month</MetricSubtext>
          </MetricCard>

          <MetricCard trend="down">
            <MetricHeader>
              <MetricLabel>{t('belonging.metrics.atRisk', 'At Risk')}</MetricLabel>
              <TrendIndicator trend="down">↓ 2</TrendIndicator>
            </MetricHeader>
            <MetricValue>
              <AnimatedCounter value={atRiskCount} />
            </MetricValue>
            <MetricSubtext>Team members need support</MetricSubtext>
          </MetricCard>
        </MetricsRow>

        {/* Main Dashboard Grid */}
        <DashboardGrid>
          {/* Team Sentiment Heatmap */}
          <ChartCard>
            <CardHeader>
              <CardTitle>{t('belonging.manager.sentimentTrend', 'Team Sentiment Trend')}</CardTitle>
              <FilterButtons>
                <FilterButton 
                  active={timeRange === 'week'}
                  onClick={() => setTimeRange('week')}
                >
                  Week
                </FilterButton>
                <FilterButton 
                  active={timeRange === 'month'}
                  onClick={() => setTimeRange('month')}
                >
                  Month
                </FilterButton>
                <FilterButton 
                  active={timeRange === 'quarter'}
                  onClick={() => setTimeRange('quarter')}
                >
                  Quarter
                </FilterButton>
              </FilterButtons>
            </CardHeader>
            
            <div style={{ marginBottom: Spacing.md }}>
              <div style={{ fontSize: FontSizes.sm + 'px', color: Colors.textSecondary, marginBottom: Spacing.sm }}>
                Daily average belonging scores
              </div>
              <HeatmapContainer>
                {sentimentData.flat().map((value, idx) => (
                  <HeatmapCell key={idx} intensity={value}>
                    {value.toFixed(1)}
                  </HeatmapCell>
                ))}
              </HeatmapContainer>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                marginTop: Spacing.sm,
                fontSize: FontSizes.xs + 'px',
                color: Colors.textTertiary
              }}>
                <span>Mon</span>
                <span>Tue</span>
                <span>Wed</span>
                <span>Thu</span>
                <span>Fri</span>
                <span>Sat</span>
                <span>Sun</span>
              </div>
            </div>
          </ChartCard>

          {/* Team Members List */}
          <ChartCard>
            <CardHeader>
              <CardTitle>{t('belonging.manager.teamMembers', 'Team Members')}</CardTitle>
            </CardHeader>
            
            <TeamList>
              {teamMembers
                .sort((a, b) => a.belongingScore - b.belongingScore)
                .map(member => (
                  <TeamMember key={member.id}>
                    <MemberAvatar score={member.belongingScore}>
                      {getInitials(member.name)}
                    </MemberAvatar>
                    <MemberInfo>
                      <MemberName>{member.name}</MemberName>
                      <MemberRole>
                        {member.role} • {formatLastCheck(member.lastPulseCheck)}
                      </MemberRole>
                    </MemberInfo>
                    <MemberScore score={member.belongingScore}>
                      {member.belongingScore.toFixed(1)}
                    </MemberScore>
                  </TeamMember>
                ))}
            </TeamList>
          </ChartCard>
        </DashboardGrid>

        {/* Action Items */}
        <ActionItemsSection>
          <CardHeader>
            <CardTitle>{t('belonging.manager.actionItems', 'Recommended Actions')}</CardTitle>
          </CardHeader>
          
          {actionItems.map(item => (
            <ActionItem key={item.id} priority={item.priority}>
              <ActionIcon priority={item.priority}>
                {item.priority === 'high' ? '' :
                 item.priority === 'medium' ? '' : ''}
              </ActionIcon>
              <ActionContent>
                <ActionTitle>{item.title}</ActionTitle>
                <ActionDescription>{item.description}</ActionDescription>
              </ActionContent>
              <ActionButton>Take Action</ActionButton>
            </ActionItem>
          ))}
        </ActionItemsSection>
      </ContentWrapper>
    </Container>
  );
};

export default BelongingManagerInsightsScreen;